<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { inject, ref } from 'vue'

import ChevronIcon from '../../../assets/icons/chevron.svg?component'
import SearchIcon from '../../../assets/icons/search.svg?component'
import { formatterProviderKey } from '../../../constants'
import { defaultFormatter } from '../../../formatters'
import {
  CampaignCreatorsActiveStatusFilter,
  type CampaignDetailsCreator,
  type CampaignOverviewViewMode,
} from '../../../types/campaign.types'
import type { TrafficSourceCosts } from '../../../types/traffic-source-costs.types'
import { debounce } from '../../../utils'
import BoxComponent from '../../atoms/BoxComponent.vue'
import FilterButtonComponent from '../../atoms/FilterButtonComponent.vue'
import FlexComponent from '../../atoms/FlexComponent.vue'
import LoadingComponent from '../../atoms/LoadingComponent.vue'
import PaginationComponent from '../../atoms/PaginationComponent.vue'
import TypographyComponent from '../../atoms/TypographyComponent.vue'

import CampaignOverviewCreator from './CampaignOverviewCreator.vue'

defineProps<{
  creatorsList?: CampaignDetailsCreator[]
  isListLoading: boolean
  isStatsLoading: boolean
  totalPages: number
  countStats?: { activeUsers: number; inactiveUsers: number }
  currency: string
  maxClicks?: number
  trafficSourceCosts?: TrafficSourceCosts[]
  viewMode: CampaignOverviewViewMode
}>()

const activeFilter = defineModel<CampaignCreatorsActiveStatusFilter>('activeFilter', {
  default: CampaignCreatorsActiveStatusFilter.Active,
})
const searchQuery = defineModel<string>('searchQuery', { default: '' })
const currentPage = defineModel<number>('currentPage', { default: 1 })
const sortField = defineModel<string>('sortField', { default: 'clicks' })
const sortOrder = defineModel<'asc' | 'desc'>('sortOrder', { default: 'desc' })

defineSlots<{
  'creator-name'?(props: { creator: CampaignDetailsCreator }): void
  'clicks-action'?(props: { creator: CampaignDetailsCreator }): void
  'costs-action'?(props: { creator: CampaignDetailsCreator }): void
  'costs-breakdown'?(props: { costs: TrafficSourceCosts[]; currency: string }): void
}>()

const inputVal = ref('')
const { formatNumber } = inject(formatterProviderKey, defaultFormatter)

const setColumnSorting = (column: string) => {
  sortOrder.value = sortField.value !== column ? 'desc' : sortOrder.value === 'asc' ? 'desc' : 'asc'
  sortField.value = column
}
const changeSearchQuery = debounce((e: Event) => {
  const target = e.target as HTMLInputElement | null
  searchQuery.value = target?.value ?? ''
  currentPage.value = 1
}, 300)

const switchFilter = (filterVal: CampaignCreatorsActiveStatusFilter) => {
  searchQuery.value = ''
  inputVal.value = ''
  sortField.value = 'clicks'
  sortOrder.value = 'desc'
  activeFilter.value = filterVal
  currentPage.value = 1
}
</script>

<template>
  <BoxComponent v-if="isStatsLoading" :m="24">
    <LoadingComponent :size="48" />
  </BoxComponent>
  <template v-else>
    <BoxComponent :mb="16">
      <FlexComponent :gap="8">
        <FilterButtonComponent
          class="filter-button"
          value="active"
          :is-active="activeFilter === CampaignCreatorsActiveStatusFilter.Active"
          @on-click="() => switchFilter(CampaignCreatorsActiveStatusFilter.Active)"
        >
          <template #label>Active</template>
          <template #count>{{ countStats ? formatNumber(countStats.activeUsers) : undefined }}</template>
        </FilterButtonComponent>
        <FilterButtonComponent
          class="filter-button"
          value="inactive"
          :is-active="activeFilter === CampaignCreatorsActiveStatusFilter.Inactive"
          @on-click="() => switchFilter(CampaignCreatorsActiveStatusFilter.Inactive)"
        >
          <template #label>Inactive</template>
          <template #count>{{ countStats ? formatNumber(countStats.inactiveUsers) : undefined }}</template>
        </FilterButtonComponent>
      </FlexComponent>
    </BoxComponent>

    <BoxComponent v-if="creatorsList" :mb="16">
      <BoxComponent :mb="8" :pl="16" :pr="16">
        <FlexComponent align="center" :gap="24" justify="space-between">
          <FlexComponent is-child flex="36 1 0%">
            <div class="search-input">
              <ElIcon :size="18" class="search-input__icon">
                <SearchIcon />
              </ElIcon>
              <input v-model="inputVal" type="text" placeholder="Creator" @input="changeSearchQuery" />
            </div>
          </FlexComponent>
          <FlexComponent is-child flex="8.3 1 0%">
            <FlexComponent :gap="4" align="center" justify="flex-end" class="pointer" @click="() => setColumnSorting('content_created')">
              <TypographyComponent variant="label-medium" text-align="right"> # Content </TypographyComponent>
              <ElIcon v-if="sortField === 'content_created'" :size="18" :class="['sort-icon', { rotated: sortOrder === 'asc' }]">
                <ChevronIcon />
              </ElIcon>
            </FlexComponent>
          </FlexComponent>
          <FlexComponent is-child flex="11.5 1 0%">
            <FlexComponent :gap="4" align="center" justify="flex-end" class="pointer" @click="() => setColumnSorting('clicks')">
              <TypographyComponent variant="label-medium" text-align="right"> Clicks </TypographyComponent>
              <ElIcon v-if="sortField === 'clicks'" :size="18" :class="['sort-icon', { rotated: sortOrder === 'asc' }]">
                <ChevronIcon />
              </ElIcon>
            </FlexComponent>
          </FlexComponent>
          <FlexComponent is-child flex="10 1 0%">
            <FlexComponent
              :gap="4"
              align="center"
              justify="flex-end"
              class="pointer"
              @click="() => setColumnSorting('estimated_media_value')"
            >
              <TypographyComponent variant="label-medium" text-align="right"> Est. media value </TypographyComponent>
              <ElIcon v-if="sortField === 'estimated_media_value'" :size="18" :class="['sort-icon', { rotated: sortOrder === 'asc' }]">
                <ChevronIcon />
              </ElIcon>
            </FlexComponent>
          </FlexComponent>
          <FlexComponent v-if="viewMode === 'admin'" is-child flex="11.4 1 0%">
            <FlexComponent
              :gap="4"
              align="center"
              justify="flex-end"
              class="pointer"
              @click="() => setColumnSorting('estimated_earning_to_cost_ratio')"
            >
              <TypographyComponent variant="label-medium" text-align="right" class="white-space-nowrap"> GP ratio </TypographyComponent>
              <ElIcon
                v-if="sortField === 'estimated_earning_to_cost_ratio'"
                :size="18"
                :class="['sort-icon', { rotated: sortOrder === 'asc' }]"
              >
                <ChevronIcon />
              </ElIcon>
            </FlexComponent>
          </FlexComponent>
          <FlexComponent v-if="viewMode === 'admin'" is-child flex="12 1 0%">
            <FlexComponent :gap="4" align="center" justify="flex-end" class="pointer" @click="() => setColumnSorting('estimated_gp')">
              <TypographyComponent variant="label-medium" text-align="right"> Est. GP </TypographyComponent>
              <ElIcon v-if="sortField === 'estimated_gp'" :size="18" :class="['sort-icon', { rotated: sortOrder === 'asc' }]">
                <ChevronIcon />
              </ElIcon>
            </FlexComponent>
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>
      <BoxComponent v-if="isListLoading" :mt="36">
        <LoadingComponent />
      </BoxComponent>
      <BoxComponent v-else-if="!creatorsList?.length" :p="24">
        <TypographyComponent is="p" variant="title-medium" text-align="center">No creators found</TypographyComponent>
      </BoxComponent>
      <template v-else>
        <BoxComponent v-for="creator in creatorsList" :key="creator.id" :mb="8">
          <CampaignOverviewCreator
            :creator="creator"
            :is-active="activeFilter === CampaignCreatorsActiveStatusFilter.Active"
            :currency="currency"
            :max-clicks="maxClicks"
            :traffic-source-costs="trafficSourceCosts"
            :view-mode="viewMode"
          >
            <template #creator-name="slotProps"><slot name="creator-name" v-bind="slotProps" /></template>
            <template #clicks-action="slotProps"><slot name="clicks-action" v-bind="slotProps" /></template>
            <template #costs-action="slotProps"><slot name="costs-action" v-bind="slotProps" /></template>
            <template #costs-breakdown="slotProps"><slot name="costs-breakdown" v-bind="slotProps" /></template>
          </CampaignOverviewCreator>
        </BoxComponent>
        <PaginationComponent v-model:current-page="currentPage" :total-pages="totalPages" />
      </template>
    </BoxComponent>
  </template>
</template>

<style scoped lang="scss">
.filter-button {
  width: calc(50% - 4px);
}

.sort-icon {
  &.rotated {
    transform: rotate(180deg);
  }
}

.search-input {
  position: relative;
  width: 168px;

  input {
    width: 100%;
    position: relative;
    z-index: 2;
    padding: 1px 4px 1px 26px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;

    font-size: 12px;
    line-height: 16px;
    font-weight: 500;

    outline: none;
    transition: border var(--transition) ease;
    cursor: pointer;

    &::placeholder {
      color: var(--primary);
      transition: color 0.15s ease;
    }

    &:focus {
      border: 1px solid var(--secondary-12);

      &::placeholder {
        color: transparent;
      }
    }
  }
}

.search-input__icon {
  position: absolute;
  top: 2px;
  left: 4px;
}
</style>
